import { AppConfigurationClient } from '@azure/app-configuration'
import appConfig from '../../app.config'
import { logError } from 'shared/AppInsight'

const connectionString = process.env
	.REACT_APP_APPCONFIG_CONNECTION_STRING as string
const client = new AppConfigurationClient(connectionString)

const fallbackConfigValues : { [id: string] : string[] } = {
	de: [],
	at: [],
	no: [],
	se: ['toggling:deposits', 'toggling:kyc', 'toggling:loans'],
	fi: ['toggling:deposits', 'toggling:kyc', 'toggling:loans'],
	ee: ['toggling:kyc', 'toggling:loans'],
	lt: ['toggling:kyc', 'toggling:loans'],
	lv: ['toggling:kyc', 'toggling:loans'],
}

function getFallbackConfigValues(country: string){
	if(fallbackConfigValues[country])
		return fallbackConfigValues[country]
	else
		return []
}

export interface ConfigError extends Error {
	response: {
		status: number
	}
}

const appConfigClient = {
	/**
	 * Adds a configuration setting.
	 * @param {string} key - The key of the configuration setting.
	 * @param {string} value - The value of the configuration setting.
	 * @param {string} [label] - The label of the configuration setting. Defaults to the country from appConfig.
	 * @usage Not in use
	 */
	async addConfigurationSetting(key: string, value: string, label?: string) {
		await client
			.addConfigurationSetting({
				key: key,
				value: value,
				label: label ?? appConfig.country,
			})
			.catch((error) => {
				console.log('Failed adding key:', error)
			})
	},

	/**
	 * Updates a configuration setting.
	 * @param {string} key - The key of the configuration setting.
	 * @param {string} value - The value of the configuration setting.
	 * @param {string} [label] - The label of the configuration setting. Defaults to the country from appConfig.
	 * @usage Not in use
	 */
	async updateConfigurationSetting(
		key: string,
		value: string,
		label?: string,
	) {
		await client
			.setConfigurationSetting({
				key: key,
				value: value,
				label: label ?? appConfig.country,
			})
			.catch((error) => {
				console.log('Failed updating key:', error)
			})
	},

	/**
	 * Gets configuration settings.
	 * @param {string} key - The key of the configuration setting.
	 * @param {string} [label] - The label of the configuration setting. Defaults to the country from appConfig.
	 * @returns {Promise<string[]>} - A promise that resolves to an array of toggles.
	 */
	async getConfigurationSetting(key: string, label?: string, retryCount: number = 0) : Promise<string[]> {
		try {
			let settings = client.listConfigurationSettings({
				keyFilter: key,
				labelFilter: label ?? appConfig.country,
				fields: ['key', 'value'],
			})
			
			let toggles: string[] = []
			for await (const setting of settings) {
				console.log('setting', setting)
				if (setting.value === 'true') toggles.push(setting.key)
			}
			return toggles as string[]
		} catch (err: any) {
			if (err.response && err.response.status === 429) {
				console.log(
					'Too many requests to app configuration service, please update subscription',
				)
				return getFallbackConfigValues(appConfig.country) //fallback values
			}
			console.log('getConfigurationSetting retryC: ', retryCount)
			console.log('Failed to get configuration settings', err)
			logError(err)
			if (retryCount < 3) {
				return await this.getConfigurationSetting(
					key,
					label,
					retryCount + 1,
				)
			}
			return getFallbackConfigValues(appConfig.country) //fallback values
		}
	},

	/**
	 * Deletes a configuration setting.
	 * @param {string} key - The key of the configuration setting.
	 * @param {string} [label] - The label of the configuration setting. Defaults to the country from appConfig.
	 * @usage Not in use
	 */
	async deleteConfigurationsSetting(key: string, label?: string) {
		await client
			.deleteConfigurationSetting({
				key: key,
				label: label ?? appConfig.country,
			})
			.catch((error) => {
				console.log('Failed deleting value:', error)
			})
	},
}

export default appConfigClient
